<template lang="pug">
.content
  
  //- TOP NAV
  ed-title-nav(ref='header' :title='$t("Import " + programLabel + " Data")')
    span(slot='action')
        router-link(v-if='$root.legacySubtab' :to='"/d/Admin/Import"')
          i.fa.fa-long-arrow-left
          span &nbsp; {{ $t('Back') + ' ' + $t('to') + ' ' + $t($root.legacySubtab) }}
  
  //- PAGE
  .container-fluid
    ed-section.active(offset)

      //- STEPS
      ed-tab-nav.col-md-12.steps.uniform(
        steps wide
        ref='steps'
        @tabSelected='enterStep'
        :check='uploadFileValid'
      )
        
        //- 1. IMPORT FILE section
        ed-tab(title="Import File")
          
          ed-section.col-md-9.active(offset=4)

            h5 Import your {{ programLabel.toLowerCase() }} data file
            
            p
              | Imports will update existing or create new {{ programsLabel.toLowerCase() }} based on looking at
              | the {{ programLabel }} IDs of existing {{ programsLabel }} in Eved.
              | Fields in the {{ programLabel }} Information, Build Agenda, Cost Centers &amp; Budget,
              | and Team Tab sections in the {{ programLabel }} module can be added or updated with this import.
            
            p If the imported data has more than one tab, the first tab will be imported.
            p Column heading must begin on Row 1.

            p Accepted file formats are .xls and .xlsx

            ed-form(:endpoint='api.env + api.$.program.upload' no-submit :submit-visible='false')
              ed-upload(button-label='Browse'
                        v-model='attachedFile'
                        :helptipHeader='"Error"'
                        :helptipBody='uploadFileError'
                        :error='!uploadFileValid'
                        :showHelptipIcon='false'
                        required
                        upload-auto
                        accept='application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        extensions='xls,xlsx'
              )


        //- 2. MATCH COLUMNS section
        ed-tab(title="Match Columns" :endpoint='api.$.program.import'
          data='data'
          :payload='data.FieldsMap'
          feedback='errors'
          validate='checkMatched'
          )
          
          ed-section.col-md-12.active
            
            .col-md-6.col-md-offset-3
              p
                | Use the table below to match the Column Headers from Row 1 of
                | your uploaded file to match to your {{ programLabel }} Fields in Eved.
              p
                | Required fields are indicated with an #[strong.text-danger *]. All required fields must have
                | a column matched to them to move to Step 3.
            p

            ed-form.col-md-10.col-md-offset-1(:endpoint='api.$.program.create' :submit-visible='false')
              table.table.table-bordered(v-show='data.FieldsMap && data.FieldsMap[0].FieldName !== null')
                thead
                  tr
                    th.col-md-3 #[br] {{ programLabel }} Field
                    th #[br] Column Header in File
                tbody
                  tr(v-for='(field, index) in updateMatches(data.FieldsMap, data.ImportedColumns)'
                    :class='{ "required": field.IsRequired && !(field.Columns && field.Columns.length) }'
                  )
                    td {{ field.FieldName }} #[strong.text-danger {{ field.IsRequired ? '*' : '' }}]
                    td
                      ed-select(
                        :options='getImportedColumns(index)'
                        :value='columnName(field.Columns, field.Type === "List")'
                        :customLabel='columnName'
                        :required='field.IsRequired'
                        @select='handleMultimatch(field, $event)'
                        @open='fetchImportedColumns(data.ImportedColumns, index)'
                      )
                        a(slot='clear'
                          v-if='field.Columns && field.Columns.length === 1 && field.Type !== "List"'
                          href='#'
                          @click.prevent='removeMatch(field)'
                        )
                          i.fas.fa-times-circle.add-icon
                        
                        template(slot='option' slot-scope='props')
                          ed-select-options(thumbnails)
                            span(slot='row1')
                              kbd {{ props.option.Letter }}
                              | &nbsp;&nbsp;
                              span {{ props.option.Name || '[None]' }}
                      
                      .additional-match(v-if='field.Type && field.Columns')
                        .form-group(v-for='column in field.Columns')
                          .col-md-6
                            kbd.pull-left {{ column.Letter }}
                            small.col-md-10 {{ column.Name || `[None]` }}
                          .col-md-5(v-if='initDetails(column, field)')
                            ed-select.inline.small(
                              :options='titles'
                              label='Text'
                              v-model='column.Details.Title'
                              placeholder='Title'
                              @open='fetchTitles'
                              :openEndpoint='api.$.program.titles'
                            )
                            ed-select.inline.small(
                              :options='permissions'
                              v-model='column.Details.Permission'
                              placeholder='Permission'
                              multiple
                              checkboxes
                              @open='fetchPermissions'
                              :openEndpoint='api.$.program.permissions'
                            )
                          small.col-md-1
                            a.pull-right(href='#' @click.prevent='removeColumn(column, field)')
                              i.fas.fa-times-circle
                          br.clear
                          br

        //- 3. REVIEW ERRORS section
        ed-tab(title="Review Errors" :optional='"Import corrected file"' :errors='errors' callback='gotErrors')
          
          ed-section.col-md-11.active
            .col-md-6.col-md-offset-3
              p
                | The table below shows the errors in your file. Please correct
                | these errors in your file and return to step 1 to upload the
                | corrected file.
            
            p
            p
            table.table.table-bordered.col-md-10.col-md-offset-1
              thead
                tr
                  th Row
                  th Column
                  th Data
                  th Column Header
                  th {{ programLabel }} Field
                  th Error Reason
              tbody
                tr(v-for='(error, i) in errors')
                  td {{ error.Row }}
                  td {{ error.Column }}
                  td {{ error.Data }}
                  td {{ error.ColumnHeader }}
                  td {{ error.FieldName }}
                  td {{ error.ErrorReason }}


        //- ========================================= FINALIZE section
        ed-tab(title="Finalize" button-label='Ok')
          
          ed-section.col-md-9.active
            .col-md-12.col-md-offset-4
              h3
                | Import Successfully Initiated
                | &nbsp;
                i.success.inline
              p
                | No errors were found in your file and your {{ programLabel.toLowerCase() }} data is being imported now.
                | You'll receive an email shortly when the import is complete and all data is updated in Eved.
              p
              p
              p

    //- BUTTONS
    ed-button.pull-right.btn.btn-primary(v-if='lastStep' :to='"/d/Admin/Import"') Ok
    ed-button.pull-right(v-if='!lastStep' @click='proceed' :disabled='!uploadFileValid || !attachedFile') {{ $t(nextLabel) }}
    ed-button.pull-right.btn.btn-secondary(v-if='!lastStep' :to='"/d/Admin/Import"') {{ $t('Cancel') }}

    //- MODALS
    ed-modal(
      :show='notAllRequiredMatched' 
      @close='notAllRequiredMatched = false'
      title='Check Unmatched Column Headers'
      :subtitle='"Please match all required " + programLabel + " Fields to Column Headers in order to move on to the next step."'
      primaryButton='Ok'
      secondaryButton='false'
    )
    ed-modal(
      :show='notAllMatched' 
      @close='notAllMatched = false'
      @confirm='skipMatch'
      title='Check Unmatched Column Headers'
      :subtitle='"Not all of your Column Headers are matched to " + programLabel + " Fields. Do you still want to continue to the next step?"'
      primaryButton='Yes'
      secondaryButton='No'
    )

</template>

<script>

import { Eved, EventBus } from '@/event-bus'
import wizardMixin from '@/core/components/edTabNav/wizardMixin'
// import customTypes from './types.json'

export default {
  name: 'ImportPrograms',

  mixins: [wizardMixin],

  methods: {
    /**
     * Updates matched column/columns list
     */
    handleMultimatch(field, value) {
      if (field.Type === 'List') {
        field.Columns = field.Columns || []
        field.Columns.push(value)
      } else {
        field.Columns = [value]
      }

      this.$forceUpdate()
    },

    /**
     * Removes 1:1 match
     */
    removeMatch(field) {
      field.Columns = []

      this.$forceUpdate()
    },

    /**
     * Removes 1:* match
     */
    removeColumn(column, field) {
      field && field.Columns.splice(field.Columns.indexOf(column), 1)

      this.$forceUpdate()
    },

    /**
     * Gets permissions list
     */
    fetchPermissions() {
      this.permissions.length || this.api(this.api.$.program.permissions).then(r => {
        this.permissions = r.data || []
      })
    },

    /**
     * Gets titles list
     */
    fetchTitles() {
      this.titles.length || this.api(this.api.$.program.titles).then(r => {
        this.titles = r.data || []
      })
    },

    /**
     * Checks if required/all fields are matched
     */
    checkMatched() {
      if (this.data.FieldsMap[0].FieldName !== null) {
        const allRequiredMatched = this.data.FieldsMap.filter(field => field.IsRequired && !(field.Columns && field.Columns.length)).length === 0
        const allMatched = this.canSkip || this.data.FieldsMap.filter(field => !field.IsRequired && !(field.Columns && field.Columns.length)).length === 0

        this.notAllRequiredMatched = !allRequiredMatched
        this.notAllMatched = allRequiredMatched && !allMatched

        if (!allRequiredMatched) {
          EventBus.$emit(Eved.GENERIC.WIZARD.INVALID, this.$refs.header.title)
        }

        return allRequiredMatched && allMatched
      }
    },

    /**
     * Can proceed to next step if not all not required are matched
     */
    skipMatch() {
      this.canSkip = true
      this.proceed()
      this.canSkip = false
    },

    gotErrors() {
      // EventBus.$emit(Eved.GENERIC.WIZARD.ERRORS, this.errors)
    },

    /**
     * Callback after each step
     */
    afterEnterStep() {
      // Activate Admin menu
      global.Page.showTabs('Admin', 'Import', '#d', null)

      // reset any pending data
      this.$store.state.pendingData = null
    }
  },

  created() {
    this.ImportedColumns = []
  },

  data () {
    return {
      canSkip: false,
      notAllMatched: false,
      notAllRequiredMatched: false,

      data: {
        FieldsMap: [
          {
            FieldName: null,
            IsRequired: true,
            Columns: []
          }
        ],
        ImportedColumns: []
      },

      errors: null,

      permissions: [],
      titles: [],

      /**
       * Column match label
       */
      columnName(option, multiple) {
        if (!option) return
        if (option.length === 0) return ''

        //
        if (multiple === false) {
          return option
        }

        // 1:* match
        if (multiple) return option && option.length + ' selected'

        // 1:1 match - [LETTER] NAME
        return option && typeof option === 'string'
                ? option
                : `[${option.Letter || option[0] && option[0].Letter}] ${option.Name || option[0] && option[0].Name || '[None]'}`
      },

      /**
       * Set details for custom matches
       */
      initDetails(column, field) {
        if (field.Alias === 'Collaborators') {
          column.Details = column.Details || {}
        }

        return field.Alias === 'Collaborators'
      },

      /**
       * Enables/disables unselected/selected columns
       */
      updateMatches(fields, columns) {
        if (!columns.length) return fields

        const matchedFields = fields
          .map(field => field.Columns)
          .reduce((fields, columns) => (fields || []).concat(columns))
          .filter(columns => columns)
          .map(column => column.Letter)

        columns = columns.map(column => {
          if (~matchedFields.indexOf(column.Letter)) {
            column.$isDisabled = true
          } else {
            delete column.$isDisabled
          }

          return column
        })

        return fields
      },

      /**
       * Only inject options @open dropdown
       * to avoid performance bottlenecks
       * when rendering large sets
       */
      fetchImportedColumns(columns, index) {
        this.$set(this.ImportedColumns, index, columns)
        this.$forceUpdate()
      },
      getImportedColumns(index) {
        return this.ImportedColumns[index] || []
      }
    }
  },

  computed: {
    programLabel() {
      let settings = JSON.parse(window.localStorage.getItem('sessionSettings'))
      return settings.CompanySettings.ProgramLabel
    },

    programsLabel() {
      let settings = JSON.parse(window.localStorage.getItem('sessionSettings'))
      return settings.CompanySettings.ProgramsLabel
    }
  }
}
</script>

<style scoped lang='scss'>
@import "~@/assets/sass/_variables.scss";

.table td:first-child {
  vertical-align: top;
  padding-top: 0.75em;
}

kbd {
  display: inline-block;
  margin-left: -12px;
  min-width: 25px;
  text-align: center;
  line-height: 1.1em;

  @include theme {
  background: v(secondary-01);
  }
}

.additional-match {
  > .form-group {
    float: left;
    width: 100%;
    margin-top: 5px;
    margin-bottom: 5px;

    @include theme {
    border-bottom: 1px solid v(background-light);
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/_modules/Import/programs.vue